import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Check, FileText, Filter, Gavel, ListChecks, LockKeyhole, MailCheck, MessageSquareText, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE } from '@/components/home/primitives';
import { InViewRing } from '@/components/home/StoryVisuals';
import { WindowFrame } from './PageKit';
import { SKIP_ENTRANCE } from '@/components/home/motionPrefs';

export function ShortlistVisual() {
  const reduced = useReducedMotion();
  const people = [
    { name: 'Maya Chen', initials: 'MC', score: 89 },
    { name: 'Jordan Lee', initials: 'JL', score: 84 },
    { name: 'Alex Morgan', initials: 'AM', score: 81 },
  ];
  return (
    <div className="relative">
      <WindowFrame label="Client review · Senior Account Manager">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-bv-mute">Shortlist for your client</p>
            <p className="mt-1 truncate font-display text-lg font-semibold text-bv-ink">Senior Account Manager</p>
          </div>
          <span className="shrink-0 rounded-full bg-bv-success-soft px-3 py-1 text-xs font-semibold text-bv-success">3 ready</span>
        </div>
        <ul className="mt-5 space-y-2.5">
          {people.map((person, i) => (
            <motion.li key={person.name} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.18, duration: 0.5, ease: EASE }} className="flex items-center gap-3 rounded-xl border border-bv-blue-line bg-white p-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-bv-ink font-display text-xs font-semibold text-white">{i + 1}</span>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bv-blue-wash text-xs font-bold text-bv-blue">{person.initials}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-bv-ink">{person.name}</p>
                <p className="truncate text-xs text-bv-mute">Resume and interview available</p>
              </div>
              <InViewRing score={person.score} size={42} stroke={4} />
            </motion.li>
          ))}
        </ul>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-bv-blue-wash p-3 text-[13px] font-medium text-bv-ink2"><LockKeyhole className="h-4 w-4 shrink-0 text-bv-coral" />Password-protected sharing with external notes</div>
        <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.5, ease: EASE }} className="mt-3 flex items-start gap-2 rounded-xl border border-bv-blue-line bg-white p-3 text-[13px] text-bv-ink">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" /><span><strong>Client note:</strong> “Keen to meet Maya this week.”</span>
        </motion.div>
      </WindowFrame>
      <div aria-hidden className="absolute -bottom-5 -left-3 hidden animate-float-slow items-center gap-2 rounded-xl bg-bv-ink px-3 py-2 text-[13px] font-semibold text-white shadow-bv-deep sm:flex">
        <Gavel className="h-4 w-4 text-bv-blue-bright" />You choose what the client sees
      </div>
    </div>
  );
}

const flow = [
  { kind: 'Trigger', label: 'Application submitted', detail: 'Resume parsed, match score calculated', icon: FileText },
  { kind: 'Rule', label: 'Resume match ≥ 80%', detail: 'Example threshold your team configures', icon: Filter },
  { kind: 'Action', label: 'Send AI interview', detail: 'Invitation delivered and logged', icon: MailCheck },
  { kind: 'Trigger', label: 'Interview completed', detail: 'Timed structured answers recorded', icon: Video },
  { kind: 'Action', label: 'Score, rank and log', detail: 'Ready for recruiter review', icon: ListChecks },
];
const kindTone: Record<string, string> = {
  Trigger: 'bg-bv-blue-wash text-bv-blue',
  Rule: 'bg-[hsl(265_80%_95%)] text-[hsl(265_50%_45%)]',
  Action: 'bg-bv-orange-soft text-bv-coral',
};

export function AutomationFlowVisual() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: '-10% 0px' });
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduced) { setActive(flow.length - 1); return; }
    if (!inView) return;
    const id = window.setInterval(() => setActive(a => (a + 1) % (flow.length + 2)), 1100);
    return () => window.clearInterval(id);
  }, [reduced, inView]);

  return (
    <div ref={ref}>
      <WindowFrame label="Stage rules · Senior Account Manager">
        <ol className="relative space-y-3">
          <span aria-hidden className="absolute bottom-6 left-[22px] top-6 w-px bg-bv-blue-line" />
          {flow.map((node, i) => {
            const done = i <= active;
            return (
              <li key={node.label} className={cn('relative flex items-center gap-3 rounded-xl border bg-white p-3 transition-all duration-500', i === active ? 'border-bv-blue/50 shadow-bv-window' : 'border-bv-blue-line')}>
                <span className={cn('relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-500', done ? 'border-transparent bg-bv-success text-white' : 'border-bv-blue-line bg-white text-transparent')} style={{ marginLeft: 11 }}>
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bv-blue-wash text-bv-blue"><node.icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-bv-ink">{node.label}</p>
                  <p className="truncate text-xs text-bv-mute">{node.detail}</p>
                </div>
                <span className={cn('hidden shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold sm:inline', kindTone[node.kind])}>{node.kind}</span>
              </li>
            );
          })}
        </ol>
      </WindowFrame>
    </div>
  );
}
